import { useCallback, useMemo, useState } from "react";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import { adaptComment, adaptStory, categoriesOf, storyListParams } from "./storyModel";

const NOTHING = () => Promise.resolve({ data: null });
const NOTHING_LIST = () => Promise.resolve({ data: [] });

// UI/API boundary for Blogs and Success Stories. GET /api/tsp/stories does
// the search/category/sort/pagination server-side (storyModel.js's
// storyListParams); comments are a separate resource (GET
// /stories/:id/comments) merged onto the open story here, since the story
// detail response itself only carries a `comment_count`.
export default function useTspStoriesData(kind, filters, openId) {
  const list = useTspApiResource(() => TspApiService.stories.list(storyListParams(filters, kind)), [kind, JSON.stringify(filters)]);
  const openStory = useTspApiResource(() => (openId ? TspApiService.stories.get(openId) : NOTHING()), [openId]);
  const openComments = useTspApiResource(() => (openId ? TspApiService.stories.listComments(openId) : NOTHING_LIST()), [openId]);

  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (call, { reloadList = true, reloadComments = false } = {}) => {
      setBusy(true);
      try {
        const result = await call();
        if (reloadList) list.reload();
        if (openId) openStory.reload();
        if (reloadComments && openId) openComments.reload();
        return result;
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openId]
  );

  const actions = useMemo(
    () => ({
      addStory: (input) =>
        run(async () => {
          const res = await TspApiService.stories.create({ title: input.title, body: input.content, category: input.category });
          return res?.data || res;
        }),
      toggleLike: (storyId) => run(() => TspApiService.stories.toggleLike(storyId)),
      addComment: (storyId, { text }) => run(() => TspApiService.stories.addComment(storyId, { body: text }), { reloadComments: true }),
    }),
    [run]
  );

  // Memoized on the underlying resources' own (stable) data references —
  // see useTspForumData.js's identical comment for why this matters: a page
  // effect keyed on `data` (accumulating pages) must only re-run when a
  // fetch actually resolves, not on every render.
  const data = useMemo(
    () =>
      list.data
        ? {
            categories: categoriesOf(kind),
            stories: list.data.map(adaptStory),
            meta: list.meta,
            openStory: openStory.data ? { ...adaptStory(openStory.data), comments: (openComments.data || []).map(adaptComment) } : null,
          }
        : null,
    [kind, list.data, list.meta, openStory.data, openComments.data]
  );

  return {
    data,
    loading: list.loading || (Boolean(openId) && (openStory.loading || openComments.loading)),
    error: list.error,
    openStoryError: openId ? openStory.error : null,
    busy,
    actions,
  };
}
